import type { ReactNode } from 'react'

import type { Specialist } from '../api/specialists'
import { formatPesewas } from '../lib/money'
import { sessionLabel } from '../lib/sessionLabel'
import { Icon } from './Icon'

/**
 * A specialist row.
 *
 * M17-T10 — NOT built on the shared `ResultRow`, deliberately, and the ticket
 * asked for it. `ResultRow` carries an icon, a title, one dot-separated meta
 * list and one badge. A specialist carries specialty, languages, consult fee,
 * qualifications, years of experience, primary hospital, a "+N hospitals"
 * count and the next clinic session — eight fields with their own hierarchy.
 *
 * Flattening those into `meta` would collapse the specialty out of the accent
 * colour, drop the "+N hospitals" pill entirely, lose the pin and clock
 * glyphs, and put six comma-separated fragments on one wrapping line at
 * phone width. That is losing information to satisfy a component, which is
 * backwards — and `ResultRow`'s own doc comment forbids the other fix
 * (adding domain props to it).
 *
 * So the geometry is aligned to `ResultRow` instead — same `--line-1` border,
 * same `--radius-r3`, same padding and type ramp — and the two read as one
 * family without one pretending to be the other.
 */
export function SpecialistRow({
  specialist,
  onOpen,
}: {
  specialist: Specialist
  onOpen: (specialistSlug: string) => void
}): ReactNode {
  const primary =
    specialist.hospitals.find((h) => h.is_primary) ?? specialist.hospitals[0]
  const extra = Math.max(0, specialist.hospitals.length - 1)
  const firstSession = primary?.sessions?.[0]
  const nextClinic = firstSession ? sessionLabel(firstSession) : null

  return (
    <button
      type="button"
      onClick={() => onOpen(specialist.slug)}
      className="grid w-full grid-cols-[44px_1fr_auto] items-start gap-3 rounded-r3 border border-line-1 bg-bg-app p-3 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <span className="grid h-11 w-11 place-items-center rounded-full bg-bg-sunken text-ink-3">
        <Icon name="user" size={20} />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-ink-1">
          {specialist.full_name}
        </span>
        <span className="block text-xs font-medium text-accent-ink">
          {specialist.specialty}
        </span>
        <span className="mt-0.5 block text-[11px] text-ink-3">
          {specialist.languages.join(' · ')}
          {specialist.consult_fee_pesewas != null && (
            <>
              {' · consult '}
              <span className="tabular-nums">
                {formatPesewas(specialist.consult_fee_pesewas)}
              </span>
            </>
          )}
        </span>
        {(specialist.qualifications?.length ||
          specialist.years_experience != null) && (
          <span className="mt-0.5 block truncate text-[11px] text-ink-3">
            {specialist.qualifications?.join(', ')}
            {specialist.qualifications?.length &&
              specialist.years_experience != null &&
              ' · '}
            {specialist.years_experience != null &&
              `${specialist.years_experience} yrs`}
          </span>
        )}
        {primary && (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-2">
            <Icon name="pin" size={11} /> {primary.name}
            {extra > 0 && (
              <span className="rounded-full bg-bg-sunken px-1.5 py-0.5 text-[10px] text-ink-2">
                +{extra} hospital{extra > 1 ? 's' : ''}
              </span>
            )}
          </span>
        )}
        {nextClinic && (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-2">
            <Icon name="clock" size={11} /> {nextClinic}
          </span>
        )}
      </span>
      <Icon name="chevr" size={16} />
    </button>
  )
}
